import type { OrganizationUserMembershipResponse } from '@np-forge/api-client/types'
import { type FC } from 'react'
import { Link } from 'react-router-dom'

import { NavigationText } from '@/components/core'
import { PlusStrokeIcon } from '@/components/core/Icon/icons'
import { SIDEBAR_TEST_IDS } from '@/constants/testIds'
import { ROUTES } from '@/routes/paths'

import { WorkspaceDropdownItemBody } from './WorkspaceDropdownItemBody'

interface OrganizationDropdownProps {
  organizations: OrganizationUserMembershipResponse[]
  currentOrganizationId: string
  isOpen: boolean
  onCreateClick: () => void
  onClose?: () => void
}

export const OrganizationDropdown: FC<OrganizationDropdownProps> = ({
  organizations,
  currentOrganizationId,
  isOpen,
  onCreateClick,
  onClose,
}) => {
  if (!isOpen) {
    return null
  }

  return (
    <div
      className="sidebar__org-dropdown"
      role="menu"
      aria-label="Organization switcher"
      data-testid={SIDEBAR_TEST_IDS.ORGANIZATION_DROPDOWN}
    >
      <div className="sidebar__org-dropdown-header" data-testid={SIDEBAR_TEST_IDS.ORGANIZATION_DROPDOWN_HEADER}>
        <p>Switch your workspaces</p>
      </div>
      <div className="sidebar__org-dropdown-list" data-testid={SIDEBAR_TEST_IDS.ORGANIZATION_DROPDOWN_LIST}>
        {organizations.map(org => {
          const isCurrentOrg = org.id === currentOrganizationId

          return (
            <Link
              key={org.id}
              to={ROUTES.WORKSPACE_LIBRARY.buildPath({ id: org.id })}
              className={`sidebar__org-dropdown-item ${isCurrentOrg ? 'sidebar__org-dropdown-item--current' : ''}`}
              role="menuitem"
              aria-current={isCurrentOrg ? 'true' : undefined}
              onClick={() => onClose?.()}
              data-testid={SIDEBAR_TEST_IDS.organizationDropdownItem(org.id)}
            >
              <WorkspaceDropdownItemBody
                name={org.name}
                avatarTestId={SIDEBAR_TEST_IDS.organizationDropdownItemAvatar(org.id)}
                nameTestId={SIDEBAR_TEST_IDS.organizationDropdownItemName(org.id)}
              />
            </Link>
          )
        })}
      </div>
      <div className="sidebar__org-dropdown-separator" />
      <button
        className="sidebar__org-dropdown-create"
        onClick={onCreateClick}
        role="menuitem"
        data-testid={SIDEBAR_TEST_IDS.ORGANIZATION_DROPDOWN_CREATE_BUTTON}
      >
        <div className="sidebar__org-dropdown-create-icon">
          <PlusStrokeIcon />
        </div>
        <NavigationText>Create new workspace</NavigationText>
      </button>
    </div>
  )
}
